import { Component, EventEmitter, Input, Output, inject } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { DateTime } from "luxon";
import { EventDate } from "../../../../../../shared/components/cards/card-manage-event/types/event-date.type";
import { TicketBatch } from "../../../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { TicketTableComponent } from "../../../../../../shared/components/ticket-table/ticket-table.component";
import { EventFormModel } from "../../event-form.form";
import { BatchEditModalComponent } from "./batch-edit-modal/batch-edit-modal.component";
import { TicketEditModalComponent } from "./ticket-edit-modal/ticket-edit-modal.component";
import { TicketBatchResponseBody } from "@services/types/ticket-batch-response.type";

type EditTicketPayload = { batchIndex: number; ticketIndex: number };
type TicketClickPayload = number | EditTicketPayload;

@Component({
  selector: "app-event-tickets-step",
  imports: [ReactiveFormsModule, TicketTableComponent],
  templateUrl: "./event-tickets-step.component.html",
  styleUrl: "./event-tickets-step.component.scss",
})
export class EventTicketsStepComponent {
  private readonly dialog = inject(MatDialog);

  @Input({ required: true }) eventForm!: FormGroup<EventFormModel>;
  @Input({ required: true }) ticketBatches: TicketBatch[] = [];
  @Input() eventId?: string;
  @Input() transitionDisabledReason = "";

  @Output() ticketBatchesChange = new EventEmitter<TicketBatch[]>();
  @Output() batchesTransitioned = new EventEmitter<TicketBatch[]>();
  @Output() batchMetadataSaved = new EventEmitter<TicketBatchResponseBody[]>();

  get isFreeEvent(): boolean {
    return !this.eventForm.controls.eventType.value.isEventPaid;
  }

  /**
   * Evento gratuito: cria o único ingresso permitido. Fluxo em 2 passos —
   * primeiro define a disponibilização (datas, via modal de datas em modo
   * "disponibilização"), depois cria o ingresso nesse período.
   */
  handleCreateSingleTicket(): void {
    const existingBatch = this.ticketBatches[0];
    if (existingBatch) {
      // Disponibilização já definida: cria/edita o ingresso direto.
      this.openTicketModal(0);
      return;
    }

    this.openAvailabilityModal(undefined, (batch) => {
      this.openTicketModal(0, undefined, batch);
    });
  }

  /** Evento gratuito: edita as datas de disponibilização do ingresso. */
  editSingleAvailability(): void {
    const batch = this.ticketBatches[0];
    if (!batch || batch.transition_closed_at) return;

    this.openAvailabilityModal(batch, (updatedBatch) => {
      const updated = this.ticketBatches.map((currentBatch, index) =>
        index === 0 ? updatedBatch : currentBatch,
      );
      this.ticketBatchesChange.emit(updated);
    });
  }

  private openAvailabilityModal(
    selectedBatch: TicketBatch | undefined,
    onSaved: (batch: TicketBatch) => void,
  ): void {
    const eventDate = this.getEventDate();
    if (!eventDate) return;

    const dialogRef = this.dialog.open(BatchEditModalComponent, {
      width: "980px",
      maxWidth: "95vw",
      panelClass: ["event-ticket-dialog", "batch-edit-dialog"],
      disableClose: true,
      data: {
        ticketBatches: this.ticketBatches,
        selectedBatch,
        eventDate,
        availabilityMode: true,
        onPersisted: (batches: TicketBatchResponseBody[]) => this.batchMetadataSaved.emit(batches),
      },
    });

    dialogRef.afterClosed().subscribe((result: TicketBatch[] | undefined) => {
      if (!result) return;
      const batch = result[0];
      if (batch) onSaved(batch);
    });
  }

  openBatchModal(selectedBatch?: TicketBatch): void {
    if (selectedBatch?.transition_closed_at) return;
    const eventDate = this.getEventDate();
    if (!eventDate) return;

    const dialogRef = this.dialog.open(BatchEditModalComponent, {
      width: "980px",
      maxWidth: "95vw",
      panelClass: ["event-ticket-dialog", "batch-edit-dialog"],
      disableClose: true,
      data: {
        ticketBatches: this.ticketBatches,
        selectedBatch,
        eventDate,
        onPersisted: (batches: TicketBatchResponseBody[]) => this.batchMetadataSaved.emit(batches),
      },
    });

    dialogRef.afterClosed().subscribe((result: TicketBatch[] | undefined) => {
      if (!result) return;

      // IMPORTANTE: sempre emite nova referência
      this.ticketBatchesChange.emit([...result]);
    });
  }

  handleOpenModalTickets(payload: TicketClickPayload): void {
    if (typeof payload === "number") {
      this.openTicketModal(payload);
      return;
    }

    this.openTicketModal(payload.batchIndex, payload.ticketIndex);
  }

  private openTicketModal(
    batchIndex: number,
    ticketIndex?: number,
    explicitBatch?: TicketBatch,
  ): void {
    const { isEventPaid } = this.eventForm.controls.eventType.value;

    const ticketBatchToUse = explicitBatch ?? this.ticketBatches[batchIndex];
    if (!ticketBatchToUse || ticketBatchToUse.transition_closed_at) return;

    const isNewImplicitBatch = !!explicitBatch;

    const ticketToEdit =
      ticketIndex !== undefined
        ? ticketBatchToUse.tickets?.[ticketIndex]
        : undefined;

    const dialogRef = this.dialog.open(TicketEditModalComponent, {
      width: "1080px",
      maxWidth: "95vw",
      panelClass: ["event-ticket-dialog", "ticket-edit-dialog"],
      disableClose: true,
      data: {
        ticketBatch: ticketBatchToUse,
        ticket: ticketToEdit,
        isTicketFree: !isEventPaid,
      },
    });

    dialogRef
      .afterClosed()
      .subscribe((updatedBatch: TicketBatch | undefined) => {
        if (!updatedBatch) return;

        // Lote implícito recém-criado (evento gratuito): vira o único lote.
        if (isNewImplicitBatch) {
          this.ticketBatchesChange.emit([updatedBatch]);
          return;
        }

        // Atualiza imutavelmente o array e emite
        const updatedTicketBatches = this.ticketBatches.map(
          (ticketBatch, currentBatchIndex) =>
            currentBatchIndex === batchIndex ? updatedBatch : ticketBatch,
        );

        this.ticketBatchesChange.emit(updatedTicketBatches);
      });
  }

  private getEventDate(): EventDate | undefined {
    const { startDateTime, endDateTime } =
      this.eventForm.controls.eventDates.getRawValue();

    const isValidDate = (d: unknown): d is Date =>
      d instanceof Date && !Number.isNaN(d.getTime());

    if (!isValidDate(startDateTime) || !isValidDate(endDateTime)) {
      return;
    }

    const start = DateTime.fromJSDate(startDateTime);
    const end = DateTime.fromJSDate(endDateTime);

    if (!start.isValid || !end.isValid) {
      return;
    }

    return {
      startDate: start.toISODate() ?? "",
      startTime: start.toFormat("HH:mm"),
      endDate: end.toISODate() ?? "",
      endTime: end.toFormat("HH:mm"),
    };
  }
}
